// ============================================================
// VCE — SmartMediaComposer (ejecutor de decisión para MEDIA PURA)
// ============================================================
// Igual que SmartComposer pero SIN SmartCard: para contextos que no
// son tarjeta (p. ej. el viewer de detalle). El motor DECIDE
// (contentProfile + composeDecision) y el render (SmartMedia) es la
// CONSECUENCIA. El consumidor describe contenido + contexto + config;
// el motor decide y ejecuta. (P1/P2/P6)
//
// F3: activa R4 (video+detail → poster+controls) y R7 (detail →
// gallery+counter) POR REGLA en la ruta detalle — antes la config fija
// las cubría; ahora la decisión del motor gobierna y la config refina.
// ============================================================
import React, { useMemo } from 'react';
import SmartMedia, { RATIO_CLASS } from './SmartMedia';
import { normalizeVisualItems } from './adapters/contentAdapter';
import { buildContentProfile } from './analysis/contentProfile';
import { composeDecision } from './decision/composeDecision';
import type {
  MediaCapabilities,
  PresentationContext,
  TenantVisualConfig,
} from './types';

interface RawMediaItem {
  type?: 'image' | 'video';
  url: string;
  poster?: string;
}

export interface SmartMediaComposerProps {
  /** Contenido crudo del consumidor */
  media?: RawMediaItem[];
  images?: string[];
  blocked?: boolean;
  alt?: string;
  /** Descriptores neutros para la decisión */
  infoLength?: number;
  hasDescription?: boolean;
  state?: string;
  actionCount?: number;
  context?: PresentationContext;
  config?: TenantVisualConfig;
  /** Poster global (contenido del consumidor: images[0]) */
  videoPoster?: string;
  /** Galería controlada (estado del consumidor — interacción, no decisión) */
  activeIndex?: number;
  onSelect?: (index: number) => void;
}

export const SmartMediaComposer: React.FC<SmartMediaComposerProps> = ({
  media,
  images,
  blocked = false,
  alt = '',
  infoLength = 0,
  hasDescription = false,
  state,
  actionCount = 0,
  context = 'detail',
  config,
  videoPoster,
  activeIndex,
  onSelect,
}) => {
  // 1. Normalizar contenido
  const items = useMemo(() => normalizeVisualItems(media, images), [media, images]);

  // 2. Analizar perfil NEUTRO
  const profile = useMemo(
    () => buildContentProfile(items, { infoLength, hasDescription, state, actionCount }, context),
    [items, infoLength, hasDescription, state, actionCount, context],
  );

  // 3. DECIDIR (el corazón del motor — R4/R7 por regla en detail)
  const decision = useMemo(() => composeDecision(profile, config), [profile, config]);

  // 4. Ejecutar: capabilities = decisión + config (config refina, P6)
  const capabilities: MediaCapabilities = {
    ...decision.capabilities,
    ...(config?.capabilities ?? {}),
  };
  const ratioClass = RATIO_CLASS[decision.mediaRatio] ?? RATIO_CLASS['3/4'];

  return (
    <SmartMedia
      media={media}
      images={images}
      blocked={blocked}
      ratioClass={ratioClass}
      alt={alt}
      capabilities={capabilities}
      config={config}
      videoPoster={videoPoster}
      activeIndex={activeIndex}
      onSelect={onSelect}
    />
  );
};

export default SmartMediaComposer;
